import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import {
  customThemesLoaded,
  findCustomTheme,
} from 'dashboard/helper/themeRegistry';

const CUSTOM_THEME_CLASSES = ['theme-pink', 'theme-navy', 'theme-gray'];
const LEGACY_THEME_KEYS = {
  pink: 'custom:preset-pink',
  navy: 'custom:preset-navy',
  gray: 'custom:preset-gray',
};

const CUSTOM_PROPERTIES = [
  '--background-color',
  '--surface-1',
  '--surface-2',
  '--surface-active',
  '--background-input-box',
  '--border-container',
  '--border-weak',
  '--border-strong',
  '--solid-1',
  '--solid-2',
  '--solid-3',
  '--solid-active',
  '--card-color',
  '--button-color',
  '--label-background',
  '--alpha-1',
  '--alpha-2',
  '--alpha-3',
  '--slate-8',
  '--slate-9',
  '--slate-10',
  '--slate-11',
  '--slate-12',
];

const rgb = hex => {
  const value = hex.replace('#', '');
  return [0, 2, 4]
    .map(offset => parseInt(value.slice(offset, offset + 2), 16))
    .join(' ');
};

const rgbAlpha = (hex, alpha) => `${rgb(hex).replaceAll(' ', ', ')}, ${alpha}`;

const clearCustomProperties = () => {
  CUSTOM_PROPERTIES.forEach(property =>
    document.body.style.removeProperty(property)
  );
};

export const previewColorTheme = theme => {
  if (!theme?.colors) return;
  const { colors } = theme;
  document.body.classList.remove('dark', ...CUSTOM_THEME_CLASSES);
  clearCustomProperties();
  if (theme.mode === 'dark') document.body.classList.add('dark');
  document.documentElement.style.setProperty('color-scheme', theme.mode);

  const properties = {
    '--background-color': rgb(colors.background),
    '--surface-1': rgb(colors.surface_1),
    '--surface-2': rgb(colors.surface_2),
    '--surface-active': rgb(colors.surface_active),
    '--background-input-box': rgbAlpha(colors.surface_2, 0.12),
    '--border-container': rgb(colors.border_container),
    '--border-weak': rgb(colors.border_weak),
    '--border-strong': rgb(colors.border_strong),
    '--solid-1': rgb(colors.surface_1),
    '--solid-2': rgb(colors.surface_2),
    '--solid-3': rgb(colors.label),
    '--solid-active': rgb(colors.surface_active),
    '--card-color': rgb(colors.card),
    '--button-color': rgb(colors.button),
    '--label-background': rgb(colors.label),
    '--alpha-1': rgbAlpha(colors.border_weak, 0.22),
    '--alpha-2': rgbAlpha(colors.text_muted, 0.14),
    '--alpha-3': rgbAlpha(colors.surface_1, 0.96),
    '--slate-8': rgb(colors.text_muted),
    '--slate-9': rgb(colors.text_muted),
    '--slate-10': rgb(colors.text_muted),
    '--slate-11': rgb(colors.text_secondary),
    '--slate-12': rgb(colors.text_primary),
  };
  Object.entries(properties).forEach(([property, value]) =>
    document.body.style.setProperty(property, value)
  );
};

export const setColorTheme = isOSOnDarkMode => {
  let selectedColorScheme =
    LocalStorage.get(LOCAL_STORAGE_KEYS.COLOR_SCHEME) || 'auto';
  if (customThemesLoaded.value && LEGACY_THEME_KEYS[selectedColorScheme]) {
    selectedColorScheme = LEGACY_THEME_KEYS[selectedColorScheme];
    LocalStorage.set(LOCAL_STORAGE_KEYS.COLOR_SCHEME, selectedColorScheme);
  }
  const customTheme = findCustomTheme(selectedColorScheme);
  if (customTheme) {
    previewColorTheme(customTheme);
    return;
  }

  if (selectedColorScheme.startsWith('custom:') && !customThemesLoaded.value) {
    return;
  }
  if (selectedColorScheme.startsWith('custom:')) {
    LocalStorage.set(LOCAL_STORAGE_KEYS.COLOR_SCHEME, 'auto');
    selectedColorScheme = 'auto';
  }
  clearCustomProperties();
  const isDarkTheme =
    (selectedColorScheme === 'auto' && isOSOnDarkMode) ||
    selectedColorScheme === 'dark' ||
    selectedColorScheme === 'navy';

  document.body.classList.remove('dark', ...CUSTOM_THEME_CLASSES);

  if (isDarkTheme) {
    document.body.classList.add('dark');
    document.documentElement.style.setProperty('color-scheme', 'dark');
  } else {
    document.documentElement.style.setProperty('color-scheme', 'light');
  }

  if (CUSTOM_THEME_CLASSES.includes(`theme-${selectedColorScheme}`)) {
    document.body.classList.add(`theme-${selectedColorScheme}`);
  }
};
